/**
 * EnvironmentalToolbarContent - Herramientas del módulo Environmental
 *
 * @version 3.0
 * @updated 2026-02-23
 * @module core/modules/environmental/components
 *
 * @description
 * Contenido de la sección modular del BottomToolbar para el módulo Environmental.
 * Se renderiza cuando el módulo ambiental está activo.
 *
 * @remarks
 * Implementa la interfaz ModuleToolbarProps para integrarse con el module-registry.
 * Las herramientas disponibles dependen del modo operacional activo.
 */

import { TreeDeciduous, Trees, Umbrella, Droplets, Waves, Droplet } from 'lucide-react';
import { useOperationalModeStore } from '../../../operational-modes';
import type { ModuleToolbarProps } from '../../../module-registry';
import './environmental-toolbar-content.css';

/**
 * Contenido del toolbar para el módulo Environmental
 *
 * @remarks
 * Renderiza dos filas de 8 botones cada una.
 * Solo las herramientas de creación están habilitadas en modo 'edit'.
 */
export function EnvironmentalToolbarContent({
  activeTool,
  onToolChange,
}: ModuleToolbarProps): JSX.Element {
  const { mode } = useOperationalModeStore();
  const isInventoryMode = mode === 'inventory';

  const handleToolClick = (toolId: string, event: React.MouseEvent<HTMLButtonElement>): void => {
    const newTool = activeTool === toolId ? null : toolId;
    void onToolChange(newTool);

    // Quitar foco del botón para que eventos keyboard (ESC) vayan al document
    event.currentTarget.blur();
  };

  return (
    <>
      {/* Primera fila: Herramientas del módulo ambiental */}
      <div className="toolbar-row">
        {/* Botón 1: Crear Árbol */}
        <button
          className={`toolbar-square-btn ${activeTool === 'create-tree' ? 'active' : ''}`}
          aria-label="Crear árbol"
          title={!isInventoryMode ? 'Debes estar en modo Inventariar para crear elementos' : 'Crear árbol'}
          onClick={(e) => handleToolClick('create-tree', e)}
          disabled={!isInventoryMode}
        >
          <TreeDeciduous size={20} />
        </button>

        {/* Botón 2: Parques, Plazas y Jardines */}
        <button
          className="toolbar-square-btn"
          aria-label="Parques, plazas y jardines"
          title="Parques, plazas y jardines (próximamente)"
          disabled
        >
          <Trees size={20} />
        </button>

        {/* Botón 3: Parasoles */}
        <button
          className="toolbar-square-btn"
          aria-label="Parasoles"
          title="Parasoles (próximamente)"
          disabled
        >
          <Umbrella size={20} />
        </button>

        {/* Botón 4: Fuentes y Bebederos */}
        <button
          className={`toolbar-square-btn ${activeTool === 'create-fountain' ? 'active' : ''}`}
          aria-label="Crear fuente o bebedero"
          title={!isInventoryMode ? 'Debes estar en modo Inventariar para crear elementos' : 'Crear fuente o bebedero'}
          onClick={(e) => handleToolClick('create-fountain', e)}
          disabled={!isInventoryMode}
        >
          <Droplets size={20} />
        </button>

        {/* Botón 5: Cursos de Agua */}
        <button
          className="toolbar-square-btn"
          aria-label="Cursos de agua"
          title="Cursos de agua (próximamente)"
          disabled
        >
          <Waves size={20} />
        </button>

        {/* Botón 6: Espejos de Agua */}
        <button
          className="toolbar-square-btn"
          aria-label="Espejos de agua"
          title="Espejos de agua (próximamente)"
          disabled
        >
          <Droplet size={20} />
        </button>

        {/* Botones 7-8: Placeholder para futuras herramientas ambientales */}
        {Array.from({ length: 2 }, (_, i) => i + 7).map((num) => (
          <button
            key={`environmental-top-${num}`}
            className="toolbar-square-btn"
            aria-label={`Herramienta ambiental ${num}`}
            disabled
          >
            {/* Sin contenido por ahora */}
          </button>
        ))}
      </div>

      {/* Segunda fila: Más herramientas ambientales */}
      <div className="toolbar-row">
        {/* Botones 9-16: Placeholder para herramientas ambientales */}
        {Array.from({ length: 8 }, (_, i) => i + 9).map((num) => (
          <button
            key={`environmental-bottom-${num}`}
            className="toolbar-square-btn"
            aria-label={`Herramienta ambiental ${num}`}
            disabled
          >
            {/* Sin contenido por ahora */}
          </button>
        ))}
      </div>
    </>
  );
}
